/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { RiErrorWarningFill } from "react-icons/ri";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileManagerAsPopup } from "@Features/files";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import { getViewerUrl } from "@Helpers";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { BsThreeDotsVertical } from "react-icons/bs";
import { createMergeFile, refreshFileManager, getFileVersions } from "@Actions/fileUpload";
import { useDispatch } from "react-redux";
import { conversionStatusTemplate, isMergeAllowed } from "@CubeComponents/constants/globalFunctions";
import TreeViewComponent from "@CubeComponents/html/TreeView";
import { fileFormatNotSupportedToast } from "@Actions/GlobalSearchAction";
import { useNavigate } from "react-router-dom";
import { constituentFilesOfMergedFileUrl } from "@Services/dataManager";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Formatters } from "slickgrid-react";

function ConstituentContent({
  fileVersionId,
  workspaceId,
  accessPermission,
  selectedSpaceTypeInFileManager,
  hierarchyId,
  MergeName,
  projectId,
}: any) {
  const { t } = useTranslation();
  const treeRef = useRef<any>(null);
  const [constituentFiles, setConstituentFiles] = useState<any[]>([]);
  // The grid's actions formatter is captured inside `columns` (useMemo with empty deps),
  // so handlers it closes over see stale state. Read via ref to always get the latest.
  const constituentFilesRef = useRef<any[]>([]);
  constituentFilesRef.current = constituentFiles;
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Recursively fetch constituent files.
   * Top-level items get __parentId = null, children get __parentId = their parent's _id.
   */
  const fetchAllConstituentFiles = async (parentVersionId: string, parentId: string | null = null): Promise<any[]> => {
    const query = new QueryBuilder().where("versions._id", "equal", parentVersionId);
    const result = await executeQuery(constituentFilesOfMergedFileUrl, query);
    const items = (result as any).result || [];

    const allItems: any[] = [];
    for (const item of items) {
      allItems.push({ ...item, __parentId: parentId });
      if (item.hasChild) {
        const children = await fetchAllConstituentFiles(item._id, item._id);
        allItems.push(...children);
      }
    }
    return allItems;
  };

  const refreshConstituentFiles = () => {
    fetchAllConstituentFiles(fileVersionId)
      .then((result) => {
        setConstituentFiles(result);
      })
      .catch((e: any) => {
        console.log(e);
        Promise.reject(e);
      });
  };

  // Fetch on mount and when fileVersionId changes
  useEffect(() => {
    refreshConstituentFiles();
  }, [fileVersionId]);

  const handleFileSelectedCancelClick = () => {
    setIsDialogVisible(false);
  };
  const handleFileSelectedButtonClick = async (selectedFiles: any) => {
    // Only use top-level file IDs for the merge operation
    const topLevelFiles = constituentFiles.filter((f: any) => !f.__parentId);
    const fileVersionIds = topLevelFiles.map((file: any) => file._id);
    const sourceFileVersionIds = Object.values(selectedFiles).map((val: any) =>
      val.isEmptyContainer ? val.id : val.fileVersion,
    );
    const fileObjArray = Object.values(selectedFiles);
    const mergedAllowed = isMergeAllowed(fileObjArray);
    const params = {
      sourceFileVersionId: [...fileVersionIds, ...sourceFileVersionIds],
      id: hierarchyId,
      originalName: MergeName,
      spaceId: workspaceId,
    };
    try {
      if (!mergedAllowed) {
        dispatch(fileFormatNotSupportedToast());
      } else {
        const response = (await createMergeFile(params)) as { data: any };
        if (response.data.status) {
          const optimisticRows = fileObjArray.map((val: any) => ({
            _id: val.isEmptyContainer ? val.id : val.fileVersion,
            fileName: val.name,
            resourceType: val.resourceType,
            fileVersionNumber: val.versionNumber ?? val.fileVersionNumber ?? 1,
            isDeleted: 0,
            conversionStatus: val.conversionStatus,
            projectId: val.project?._id ?? projectId,
            hasChild: val.resourceType === "RESOURCE_TYPE_MERGED_FILE",
            __parentId: null,
          }));
          setConstituentFiles((current) => [...current, ...optimisticRows]);
          dispatch(refreshFileManager(true));
          dispatch(getFileVersions({ resourceType: "RESOURCE_TYPE_MERGED_FILE", fileId: hierarchyId }));
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_SUCCESS,
              content: t("Added successfully."),
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_WARNING,
              content: untranslated(response.data.errorMessage),
            }),
          );
        }
        handleFileSelectedCancelClick();
      }
    } catch (error) {
      console.log(error);
      Promise.reject(error);
    }
  };
  const handleRemoveFilemerged = async (file: any) => {
    const { _id }: any = file;
    // Only use top-level file IDs for the merge operation
    const topLevelFiles = constituentFilesRef.current.filter((f: any) => !f.__parentId);
    const sourceFileVersionId = topLevelFiles.filter((f: any) => f._id !== _id).map((f: any) => f._id);
    const params = {
      sourceFileVersionId,
      id: hierarchyId,
      originalName: MergeName,
      spaceId: workspaceId,
    };
    try {
      await createMergeFile(params);
      dispatch(refreshFileManager(true));
      dispatch(getFileVersions({ resourceType: "RESOURCE_TYPE_MERGED_FILE", fileId: hierarchyId }));
      setConstituentFiles((current) => current.filter((f: any) => f._id !== _id && f.__parentId !== _id));
      dispatch(
        showToastNotification({
          type: TOAST_TYPE_SUCCESS,
          content: t("Removed successfully."),
        }),
      );
    } catch (error) {
      console.log(error);
      Promise.reject(error);
    }
  };
  const handleOpenLink = (file: any) => {
    const location: any = getViewerUrl({
      fileVersionId: file._id,
      spaceId: workspaceId,
      projectId: file.projectId,
    });
    navigate(location);
  };
  const getPopOverItems = (file: any) => {
    const items: any = [];
    items.push({
      text: "Open",
      id: "open",
      disabled: file.isDeleted === 1 || file.fileVersionNumber == 0,
    });
    // Only top-level items (no parent) can be removed from the merge
    if (!file.__parentId) {
      items.push({
        text: "Remove",
        id: "remove",
      });
    }
    return items;
  };
  const handleSelectedOption = async (args: any, file: any) => {
    if (args.item.properties.id == "open") {
      handleOpenLink(file);
    } else if (args.item.properties.id == "remove") {
      await handleRemoveFilemerged(file);
    }
  };

  const actionsFormatter = (_row: any, _cell: any, _value: any, _col: any, data: any) => {
    if (!["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"].includes(data.resourceType)) {
      return document.createElement("div");
    }
    const container = document.createElement("div");
    container.className = "flex mt-px";
    const root = createRoot(container);
    root.render(
      <SelectDropDown
        select={(args) => handleSelectedOption(args, data)}
        items={getPopOverItems(data)}
        cssClass="e-caret-hide border-none bg-white p-0"
      >
        <BsThreeDotsVertical />
      </SelectDropDown>,
    );
    return container;
  };

  const versionFormatter = (_row: any, _cell: any, _value: any, _col: any, data: any) => {
    const container = document.createElement("div");
    container.className = "flex gap-2 items-center";
    const root = createRoot(container);
    root.render(
      <>
        <span>{`V${data.fileVersionNumber}`}</span>
        {data.isDeleted === 1 && (
          <ToolTipComponent showTipPointer position="bottom" content={t("This file is missing")}>
            <span>
              <RiErrorWarningFill className="text-red-500" size={16} />
            </span>
          </ToolTipComponent>
        )}
        <span>{conversionStatusTemplate(data)}</span>
      </>,
    );
    return container;
  };

  const columns = useMemo(
    () => [
      {
        id: "actions",
        field: "resourceType",
        name: "",
        maxWidth: 40,
        formatter: actionsFormatter,
        resizable: false,
      },
      {
        id: "fileName",
        field: "fileName",
        name: t("File Name"),
        formatter: Formatters.tree,
      },
      {
        id: "version",
        field: "fileVersionNumber",
        name: t("Version"),
        maxWidth: 120,
        formatter: versionFormatter,
      },
    ],

    [],
  );

  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };
  const handleAddBtnClick = () => {
    if (!accessPermission?.write && selectedSpaceTypeInFileManager !== "external") {
      showToast();
      return;
    }
    setIsDialogVisible(true);
  };
  return (
    <>
      {isDialogVisible && (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={handleFileSelectedCancelClick}
          workspaceId={workspaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          heading={`Add Files in ${MergeName}`}
          disableFiles={constituentFiles.map((file: any) => file.fileVersionId).concat(fileVersionId)}
          addEmptyContainerToList
          minimumFiles={1}
        />
      )}
      <div>
        <div>
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleAddBtnClick}>
            {t("Add")}
          </ButtonComponent>
        </div>
        <div className="pt-4">
          {constituentFiles.length > 0 && (
            <TreeViewComponent
              ref={treeRef}
              treeData={constituentFiles}
              columns={columns}
              columnId="fileName"
              identifierPropName="_id"
              parentPropName="__parentId"
              disableHeader
              height={400}
            />
          )}
        </div>
      </div>
    </>
  );
}
export default ConstituentContent;
